// Lubbock 3D Printing — quote pricing (website side).
// The quote server only reports time and grams; every dollar amount is computed here.
//
// To change a price, edit RATES below and re-upload this file. That is the only place rates live.
// Quotes created by the builder store a copy of the rates they were quoted at
// (item.settings.rateCard), so a rate change never reprices a part that was already quoted.
// Verified/paid quotes always show agreedPricing exactly as the operator sent it.

export const RATES = {
  version: '2026-10-24b',
  hourly: 2.5,                       // $ per print hour, every hour
  perGram: { PLA: 0.022, PETG: 0.022, ABS: 0.022, TPU: 0.045 },
  defaultMaterial: 'PLA',            // "Recommend one" is estimated as this
  minimumOrder: 15,                  // applied before delivery
  delivery: { pickup: 0, local: 10, ship: null }, // ship = operator-entered USPS rate
  chargeColorChangeTime: true,       // AMS swap time billed at the hourly rate
  chargePurgeWaste: true,            // purge / prime tower grams billed per gram
  chargePrepTime: true,              // setup (bed leveling, calibration, heat-up) billed at the hourly rate
};

export const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
export const money = (n) =>
  n == null || Number.isNaN(n) ? '—' : '$' + round2(n).toFixed(2);

export function fmtDuration(seconds) {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
}

const fmtGrams = (g) => `${Math.round(Number(g) || 0)} g`;

// The rate card an item was quoted at, falling back to today's rates.
export function ratesFor(item) {
  const card = item && item.settings && item.settings.rateCard;
  if (card && typeof card === 'object' && typeof card.hourly === 'number') {
    return { ...RATES, ...card, perGram: { ...RATES.perGram, ...(card.perGram || {}) } };
  }
  return RATES;
}

function gramRate(material, rates) {
  const key = String(material || '').toUpperCase();
  return rates.perGram[key] ?? rates.perGram[rates.defaultMaterial];
}

// Material used by a filament slot of an item. The server names it on each slot;
// older results fall back to the item's settings.
export function slotMaterial(item, slot, filament) {
  if (filament && filament.material) return filament.material;
  if (item.multicolor) return 'PLA';
  const support = item.settings && item.settings.supportMaterial;
  if (slot > 1 && support) return support;
  return item.material || RATES.defaultMaterial;
}

// Price one line item (all copies). Returns null while the part is still slicing.
export function priceItem(item) {
  const rates = ratesFor(item);
  const qty = Math.max(1, Number(item.qty) || 1);

  if (item.override != null && item.override !== '') {
    return { n: item.n, amount: round2(item.override), override: true, qty };
  }
  const s = item.slice;
  if (!s || s.status !== 'done') return null;

  // s.seconds is the total (print + color changes + setup); the parts are listed for transparency.
  const printSeconds = s.printSeconds ?? s.seconds ?? 0;
  const ccSeconds = rates.chargeColorChangeTime ? s.colorChangeSeconds || 0 : 0;
  const prepSeconds = rates.chargePrepTime ? s.prepSeconds || 0 : 0;
  const pricedSeconds = s.printSeconds == null ? s.seconds || 0 : printSeconds + ccSeconds + prepSeconds;
  const hours = (pricedSeconds / 3600) * qty;
  const timeAmount = round2(hours * rates.hourly);

  let filamentAmount = 0;
  let pricedGrams = 0;
  const filaments = Array.isArray(s.filaments) && s.filaments.length
    ? s.filaments
    : [{ slot: 1, grams: s.grams || 0, modelGrams: s.modelGrams ?? s.grams ?? 0 }];
  for (const f of filaments) {
    const g = (rates.chargePurgeWaste ? f.grams : f.modelGrams ?? f.grams) || 0;
    pricedGrams += g * qty;
    filamentAmount += g * qty * gramRate(slotMaterial(item, f.slot, f), rates);
  }
  filamentAmount = round2(filamentAmount);

  return {
    n: item.n,
    qty,
    hours,
    pricedSeconds,
    pricedGrams,
    timeAmount,
    filamentAmount,
    amount: round2(timeAmount + filamentAmount),
  };
}

function supportOf(item) {
  const f = item.slice && Array.isArray(item.slice.filaments) ? item.slice.filaments.find((x) => x.role === 'support') : null;
  return (f && f.material) || (!item.multicolor && item.settings && item.settings.supportMaterial) || null;
}

export function itemTitle(item) {
  const qty = Math.max(1, Number(item.qty) || 1);
  const slots = item.slice && Array.isArray(item.slice.filaments) ? item.slice.filaments.length : 1;
  const mat = item.multicolor ? 'PLA' : item.material || 'PLA';
  const support = supportOf(item);
  let desc = `${qty} × ${mat}`;
  if (item.multicolor) desc += `, ${slots} colors`;
  if (support) desc += ` + ${support} support interface`;
  return `${item.name} (${desc})`;
}

// Human breakdown for one copy, e.g.
// "2h 24m printing + 34m color changes (48 swaps) + 7m setup · 52 g in the part + 11 g purge"
export function breakdownText(item) {
  const s = item.slice;
  if (!s || s.status !== 'done') return '';
  const parts = [`${fmtDuration(s.printSeconds ?? s.seconds)} printing`];
  if (s.colorChanges) {
    parts.push(`${fmtDuration(s.colorChangeSeconds)} ${supportOf(item) && !item.multicolor ? 'filament swaps' : 'color changes'} (${s.colorChanges} swaps)`);
  }
  if (s.prepSeconds) parts.push(`${fmtDuration(s.prepSeconds)} setup`);
  let grams = `${fmtGrams(s.modelGrams ?? s.grams)} in the part`;
  if (s.wasteGrams) grams += ` + ${fmtGrams(s.wasteGrams)} purge`;
  return `${parts.join(' + ')} · ${grams}`;
}

function deliveryFee(quote, rates) {
  const d = quote.delivery;
  if (d === 'ship') {
    const ship = quote.charges && quote.charges.shipping;
    return ship == null ? null : round2(ship);
  }
  return round2(rates.delivery[d] ?? 0);
}

// Price a whole quote as returned by GET /quotes/:id (or a list summary: its `lines` are used as items).
// Result: { items, lines, extras, discount, subtotal, delivery, total, pending, shippingTBD, locked }
export function priceQuote(quote) {
  if (!quote) return null;

  // Verified, paid and later: show exactly what the operator agreed to.
  if (quote.agreedPricing) {
    const a = quote.agreedPricing;
    return { ...a, extras: a.extras || [], lines: a.lines || [], delivery: a.shipping, locked: true, pending: false, shippingTBD: false };
  }

  const items = Array.isArray(quote.items) ? quote.items : Array.isArray(quote.lines) ? quote.lines : [];
  const rates = items.length ? ratesFor(items[0]) : RATES;
  const charges = quote.charges || { extras: [], discount: 0, shipping: null };

  let pending = false;
  const priced = items.map((it) => {
    const p = priceItem(it);
    if (!p) pending = true;
    return { n: it.n, title: it.name ? itemTitle(it) : '', amount: p ? p.amount : null, detail: p };
  });

  const itemsSum = round2(priced.reduce((a, p) => a + (p.amount || 0), 0));
  const extras = Array.isArray(charges.extras) ? charges.extras : [];
  const extrasSum = round2(extras.reduce((a, e) => a + (Number(e.amount) || 0), 0));

  const beforeMin = round2(itemsSum + extrasSum);
  const minimumTopUp =
    !pending && beforeMin > 0 && beforeMin < rates.minimumOrder ? round2(rates.minimumOrder - beforeMin) : 0;
  const discount = round2(Math.min(Number(charges.discount) || 0, beforeMin + minimumTopUp));
  const delivery = deliveryFee(quote, rates);
  const shippingTBD = quote.delivery === 'ship' && delivery == null;
  const subtotal = round2(beforeMin + minimumTopUp - discount);

  const lines = [];
  if (minimumTopUp > 0) lines.push({ title: `Minimum order ($${rates.minimumOrder})`, amount: minimumTopUp });

  return {
    items: priced,
    lines,
    extras,
    discount,
    discountReason: charges.discountReason || '',
    subtotal,
    delivery,
    total: shippingTBD || pending ? null : round2(subtotal + (delivery || 0)),
    pending,
    shippingTBD,
    locked: false,
  };
}

// Body for POST /quotes/:id/verify — { pricing: {...} } in the shape the server checks:
// sum(items) + sum(lines) + sum(charges.extras) − discount + shipping == total
export function verifyPricing(quote) {
  const p = priceQuote({ ...quote, agreedPricing: null });
  if (!p || p.pending) throw new Error('Every part must finish slicing (or have a price override) before verifying.');
  if (p.shippingTBD) throw new Error('Enter the USPS shipping rate before verifying.');
  const shipping = round2(p.delivery || 0);
  const items = p.items.map((i) => ({ n: i.n, amount: round2(i.amount), title: i.title }));
  const lines = p.lines.filter((l) => l.amount > 0).map((l) => ({ title: l.title, amount: round2(l.amount) }));
  const extrasSum = (p.extras || []).reduce((a, e) => a + (Number(e.amount) || 0), 0);
  const total = round2(
    items.reduce((a, i) => a + i.amount, 0) + lines.reduce((a, l) => a + l.amount, 0) + extrasSum - p.discount + shipping
  );
  return { pricing: { items, lines, discount: p.discount, shipping, total } };
}

// The rate card stored on each new item so later rate changes don't reprice it.
export function rateCardSnapshot() {
  const { version, hourly, perGram, minimumOrder, delivery, chargeColorChangeTime, chargePurgeWaste, chargePrepTime } = RATES;
  return { version, hourly, perGram: { ...perGram }, minimumOrder, delivery: { ...delivery }, chargeColorChangeTime, chargePurgeWaste, chargePrepTime };
}

// Classic (non-module) scripts such as the quotes portal use window.L3DPricing.
if (typeof window !== 'undefined') {
  window.L3DPricing = { RATES, priceQuote, priceItem, verifyPricing, breakdownText, itemTitle, money, fmtDuration, rateCardSnapshot };
  window.dispatchEvent(new Event('l3d-pricing-ready'));
}
